import { createTheme, type Theme } from '@mui/material/styles';

import { DARK_PALETTE, LIGHT_PALETTE } from './palette';
import type { ThemeMode } from './ThemeMode';

export const createAppTheme = (mode: ThemeMode): Theme => {
  const isDark = mode === 'dark';
  const palette = isDark ? DARK_PALETTE : LIGHT_PALETTE;

  return createTheme({
    palette,
    shape: { borderRadius: 10 },
    typography: {
      fontFamily: '"Inter", "Segoe UI", system-ui, -apple-system, sans-serif',
      h4: { fontWeight: 640, letterSpacing: '-0.021em', fontSize: '1.6rem' },
      h5: { fontWeight: 640, letterSpacing: '-0.018em' },
      h6: { fontWeight: 620, letterSpacing: '-0.012em', fontSize: '1.05rem' },
      subtitle2: { fontWeight: 600 },
      body2: { fontSize: '0.875rem' },
      overline: { letterSpacing: '0.09em', fontWeight: 600, fontSize: '0.68rem' },
      button: { textTransform: 'none', fontWeight: 600 },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          '*::-webkit-scrollbar': { width: 10, height: 10 },
          '*::-webkit-scrollbar-thumb': {
            backgroundColor: isDark ? 'rgba(255,255,255,0.14)' : 'rgba(0,0,0,0.18)',
            borderRadius: 8,
            border: '2px solid transparent',
            backgroundClip: 'content-box',
          },
          'html, body, #root': { height: '100%' },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: { backgroundImage: 'none' },
          outlined: { borderColor: isDark ? 'rgba(255,255,255,0.09)' : 'rgba(15,18,21,0.10)' },
        },
      },
      MuiAppBar: {
        defaultProps: { elevation: 0, color: 'transparent' },
        styleOverrides: {
          root: {
            backdropFilter: 'blur(10px)',
            backgroundColor: isDark ? 'rgba(15,18,21,0.82)' : 'rgba(255,255,255,0.86)',
            borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.09)' : 'rgba(15,18,21,0.10)'}`,
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: isDark ? '#12161b' : '#ffffff',
            borderRight: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : 'rgba(15,18,21,0.09)'}`,
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            paddingTop: 7,
            paddingBottom: 7,
            '&.Mui-selected': { fontWeight: 600 },
          },
        },
      },
      MuiListItemIcon: { styleOverrides: { root: { minWidth: 34 } } },
      MuiTableCell: {
        styleOverrides: {
          root: { borderColor: isDark ? 'rgba(255,255,255,0.07)' : 'rgba(15,18,21,0.08)' },
          head: {
            fontWeight: 620,
            fontSize: '0.75rem',
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
            color: isDark ? '#93a1ae' : '#5b6770',
            backgroundColor: isDark ? '#12161b' : '#fafbfa',
            whiteSpace: 'nowrap',
          },
          body: { fontSize: '0.86rem' },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: { '&:last-child td': { borderBottom: 0 } },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { fontWeight: 570, letterSpacing: '0.005em' },
          sizeSmall: { height: 21, fontSize: '0.72rem' },
        },
      },
      MuiTooltip: {
        defaultProps: { arrow: true },
        styleOverrides: { tooltip: { fontSize: '0.76rem' } },
      },
      MuiButton: { defaultProps: { disableElevation: true } },
      MuiInputBase: {
        styleOverrides: {
          // Chrome paints autofilled inputs its own bright blue, ignores `background-color`, and honours
          // only an inset shadow large enough to cover the field.
          //
          // This has to live on the input component, not in `MuiCssBaseline`. A global
          // `input:-webkit-autofill` is specificity (0,1,1) and loses to MUI's own
          // `.MuiInputBase-input:-webkit-autofill` at (0,2,0) — which is why a first attempt left the
          // password field blue while the e-mail field, no longer in the autofill state, looked fixed.
          // The screenshot caught it; the stylesheet on its own did not.
          input: {
            '&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus, &:-webkit-autofill:active':
              {
                // `!important` is load-bearing, not laziness. Chrome applies its own
                // `-internal-autofill-selected` style, which is a UA rule at the same specificity and
                // later in the cascade — a normal declaration loses to it every time. Verified by reading
                // the matched rules on the live field, not assumed.
                WebkitBoxShadow: `0 0 0 100px ${palette.background?.paper ?? 'transparent'} inset !important`,
                WebkitTextFillColor: `${palette.text?.primary ?? 'inherit'} !important`,
                caretColor: palette.text?.primary,
                // Chrome fades its own colour in over this; without it the blue flashes on every load.
                transition: 'background-color 100000s ease-in-out 0s',
              },
          },
        },
      },
      MuiTextField: { defaultProps: { size: 'small' } },
      MuiAlert: { styleOverrides: { root: { borderRadius: 10 } } },
      MuiSkeleton: { defaultProps: { animation: 'wave' } },
      MuiDialog: { styleOverrides: { paper: { backgroundImage: 'none' } } },
      MuiLinearProgress: { styleOverrides: { root: { borderRadius: 999 } } },
    },
  });
};
